import React, { useState, useCallback, useEffect } from 'react';
import { CheckCircle, Building2, Wallet, Copy, Loader2, Check, User, Briefcase, Shield, ArrowRight, ArrowLeft } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { useAuth } from '../contexts/AuthContext';
import SumsubWebSdk from '@sumsub/websdk-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GetStarted = () => {
  const navigate = useNavigate();
  const { user, token, refreshUser } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [accounts, setAccounts] = useState(null);
  const [accountType, setAccountType] = useState('personal');
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    country: 'GB',
    business_name: '',
    registration_number: '',
    business_type: 'llc'
  });
  const [selectedNetworks, setSelectedNetworks] = useState(['ethereum', 'bsc', 'polygon']);
  const [expandedWallet, setExpandedWallet] = useState(null);
  
  // Sumsub state
  const [sumsubToken, setSumsubToken] = useState(null);
  const [kycStatus, setKycStatus] = useState(null);
  const [kycLoading, setKycLoading] = useState(false);
  const [kycError, setKycError] = useState(null);

  const countries = [
    { code: 'GB', name: 'United Kingdom', flag: '🇬🇧' },
    { code: 'DE', name: 'Germany', flag: '🇩🇪' },
    { code: 'FR', name: 'France', flag: '🇫🇷' },
    { code: 'ES', name: 'Spain', flag: '🇪🇸' },
    { code: 'IT', name: 'Italy', flag: '🇮🇹' },
    { code: 'NL', name: 'Netherlands', flag: '🇳🇱' },
    { code: 'BE', name: 'Belgium', flag: '🇧🇪' },
    { code: 'US', name: 'United States', flag: '🇺🇸' }
  ];

  const networks = [
    { id: 'ethereum', name: 'Ethereum', symbol: 'ETH', icon: '⟠' },
    { id: 'bsc', name: 'BNB Chain', symbol: 'BNB', icon: '🔶' },
    { id: 'polygon', name: 'Polygon', symbol: 'MATIC', icon: '🟣' },
    { id: 'avalanche', name: 'Avalanche', symbol: 'AVAX', icon: '🔺' },
    { id: 'arbitrum', name: 'Arbitrum', symbol: 'ARB', icon: '🔵' },
    { id: 'optimism', name: 'Optimism', symbol: 'OP', icon: '🔴' },
    { id: 'fantom', name: 'Fantom', symbol: 'FTM', icon: '👻' },
    { id: 'base', name: 'Base', symbol: 'BASE', icon: '🔷' },
    { id: 'bitcoin', name: 'Bitcoin', symbol: 'BTC', icon: '₿' },
    { id: 'solana', name: 'Solana', symbol: 'SOL', icon: '◎' }
  ];

  // Pre-fill form with user data
  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        full_name: user.full_name || '',
        email: user.email || ''
      }));
      setAccountType(user.account_type || 'personal');
    }
  }, [user]);

  // Check KYC status on mount
  useEffect(() => {
    const checkKycStatus = async () => {
      if (!token) return;
      try {
        const response = await axios.get(`${API}/kyc/sumsub/status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setKycStatus(response.data);
      } catch (error) {
        console.log('KYC status not available yet');
      }
    };
    checkKycStatus();
  }, [token]);

  const toggleNetwork = (networkId) => {
    if (selectedNetworks.includes(networkId)) {
      setSelectedNetworks(selectedNetworks.filter(n => n !== networkId));
    } else {
      setSelectedNetworks([...selectedNetworks, networkId]);
    }
  };

  const createAccounts = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/banking/onboarding/create-complete`, {
        ...formData,
        account_type: accountType,
        networks: selectedNetworks
      });
      setAccounts(response.data);
      setStep(3);
      toast.success('Bank account and crypto wallets created successfully!');
    } catch (error) {
      console.error('Error creating accounts:', error);
      toast.error('Failed to create accounts. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied!`);
  };

  // Fetch Sumsub access token
  const fetchSumsubToken = useCallback(async () => {
    if (!token) return null;
    
    setKycLoading(true);
    try {
      const response = await axios.post(`${API}/kyc/sumsub/access-token`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSumsubToken(response.data.token);
      return response.data.token;
    } catch (error) {
      console.error('Error fetching Sumsub token:', error);
      // Set a specific error state for user-friendly message
      if (error.response?.status === 500) {
        setKycError('KYC service temporarily unavailable. Please try again later or contact support.');
      } else {
        setKycError('Failed to initialize KYC. Please try again.');
      }
      toast.error('Failed to initialize KYC verification');
      return null;
    } finally {
      setKycLoading(false);
    }
  }, [token]);

  // Handle token expiration
  const handleTokenExpiration = useCallback(async () => {
    const newToken = await fetchSumsubToken();
    return newToken;
  }, [fetchSumsubToken]);

  // Start KYC verification
  const startKycVerification = async () => {
    await fetchSumsubToken();
  };

  // Handle Sumsub messages
  const handleSumsubMessage = useCallback((type, payload) => {
    console.log('Sumsub message:', type, payload);
    
    if (type === 'idCheck.onStepCompleted') {
      toast.success('Step completed!');
    }
    
    if (type === 'idCheck.applicantStatus') {
      if (payload.reviewStatus === 'completed') {
        if (payload.reviewResult?.reviewAnswer === 'GREEN') {
          toast.success('KYC verification approved!');
          setKycStatus({ status: 'completed', review_answer: 'GREEN', kyc_verified: true });
          if (refreshUser) refreshUser();
        }
      }
    }
  }, [refreshUser]);

  // Handle Sumsub errors
  const handleSumsubError = useCallback((error) => {
    console.error('Sumsub error:', error);
    toast.error('KYC verification encountered an error. Please try again.');
  }, []);

  const canProceed = accountType === 'personal' 
    ? (formData.full_name && formData.email && formData.country && selectedNetworks.length > 0)
    : (formData.full_name && formData.email && formData.country && formData.business_name && formData.registration_number && selectedNetworks.length > 0);

  const stepLabels = ['Account & Wallets', 'Review', 'Success', 'KYC Verification'];

  return (
    <div data-testid="get-started-page" className="min-h-screen py-6 px-3 md:px-4">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-6">
          <h1 className="text-2xl md:text-3xl font-bold text-white mb-1">Get Started with Onerik</h1>
          <p className="text-white/60 text-sm">Complete your account setup and verification</p>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-center mb-6">
          {[1, 2, 3, 4].map((s, idx) => (
            <React.Fragment key={s}>
              <div className={`flex items-center justify-center w-10 h-10 rounded-full border-2 text-sm transition-all ${
                step >= s 
                  ? 'bg-primary border-primary text-white' 
                  : 'border-white/20 text-white/40'
              }`}>
                {step > s ? <CheckCircle className="w-5 h-5" /> : s}
              </div>
              {idx < 3 && (
                <div className={`h-0.5 w-8 md:w-12 mx-1 transition-all ${
                  step > s ? 'bg-primary' : 'bg-white/20'
                }`}></div>
              )}
            </React.Fragment>
          ))}
        </div>
        <div className="text-center text-xs text-white/40 mb-6">
          {stepLabels[step - 1]}
        </div>

        {/* Step 1: Personal Info + Network Selection */}
        {step === 1 && (
          <div className="space-y-4">
            {/* Account Type Selection */}
            <div className="bg-black/40 backdrop-blur-xl border border-white/10 rounded-xl p-4 md:p-5">
              <h2 className="text-lg font-semibold text-white mb-3">Account Type</h2>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => setAccountType('personal')}
                  data-testid="account-type-personal"
                  className={`p-4 rounded-lg border-2 transition-all ${
                    accountType === 'personal'
                      ? 'border-primary bg-primary/10'
                      : 'border-white/10 bg-black/50 hover:border-white/20'
                  }`}
                >
                  <div className="flex flex-col items-center gap-2">
                    <User className={`w-6 h-6 ${accountType === 'personal' ? 'text-primary' : 'text-white/60'}`} />
                    <div className="text-sm font-medium text-white">Personal</div>
                    <div className="text-xs text-white/60">For individuals</div>
                  </div>
                </button>
                <button
                  onClick={() => setAccountType('business')}
                  data-testid="account-type-business"
                  className={`p-4 rounded-lg border-2 transition-all ${
                    accountType === 'business'
                      ? 'border-primary bg-primary/10'
                      : 'border-white/10 bg-black/50 hover:border-white/20'
                  }`}
                >
                  <div className="flex flex-col items-center gap-2">
                    <Briefcase className={`w-6 h-6 ${accountType === 'business' ? 'text-primary' : 'text-white/60'}`} />
                    <div className="text-sm font-medium text-white">Business</div>
                    <div className="text-xs text-white/60">For companies</div>
                  </div>
                </button>
              </div>
            </div>

            <div className="bg-black/40 backdrop-blur-xl border border-white/10 rounded-xl p-4 md:p-5">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-10 h-10 bg-primary/20 rounded-lg flex items-center justify-center">
                  {accountType === 'personal' ? <User className="w-5 h-5 text-primary" /> : <Briefcase className="w-5 h-5 text-primary" />}
                </div>
                <h2 className="text-lg font-semibold text-white">
                  {accountType === 'personal' ? 'Personal Information' : 'Business Information'}
                </h2>
              </div>

              <div className="space-y-4">
                {accountType === 'business' && (
                  <>
                    <div>
                      <label className="text-xs text-white/60 mb-1.5 block">Business Name</label>
                      <input
                        type="text"
                        value={formData.business_name}
                        onChange={(e) => setFormData({...formData, business_name: e.target.value})}
                        placeholder="Acme Corporation"
                        data-testid="business-name-input"
                        className="w-full bg-black/50 border border-white/10 focus:border-primary/50 rounded-lg h-10 px-3 text-sm text-white placeholder:text-white/20 transition-all focus:ring-1 focus:ring-primary/50 focus:outline-none"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs text-white/60 mb-1.5 block">Registration Number</label>
                        <input
                          type="text"
                          value={formData.registration_number}
                          onChange={(e) => setFormData({...formData, registration_number: e.target.value})}
                          placeholder="12345678"
                          data-testid="registration-number-input"
                          className="w-full bg-black/50 border border-white/10 focus:border-primary/50 rounded-lg h-10 px-3 text-sm text-white placeholder:text-white/20 transition-all focus:ring-1 focus:ring-primary/50 focus:outline-none"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-white/60 mb-1.5 block">Business Type</label>
                        <select
                          value={formData.business_type}
                          onChange={(e) => setFormData({...formData, business_type: e.target.value})}
                          data-testid="business-type-select"
                          className="w-full bg-black/50 border border-white/10 focus:border-primary/50 rounded-lg h-10 px-3 text-sm text-white transition-all focus:ring-1 focus:ring-primary/50 focus:outline-none"
                        >
                          <option value="llc">LLC</option>
                          <option value="corporation">Corporation</option>
                          <option value="partnership">Partnership</option>
                          <option value="sole_proprietor">Sole Proprietor</option>
                        </select>
                      </div>
                    </div>
                  </>
                )}

                <div>
                  <label className="text-xs text-white/60 mb-1.5 block">
                    {accountType === 'personal' ? 'Full Name' : 'Representative Name'}
                  </label>
                  <input
                    type="text"
                    value={formData.full_name}
                    onChange={(e) => setFormData({...formData, full_name: e.target.value})}
                    placeholder="John Doe"
                    data-testid="full-name-input"
                    className="w-full bg-black/50 border border-white/10 focus:border-primary/50 rounded-lg h-10 px-3 text-sm text-white placeholder:text-white/20 transition-all focus:ring-1 focus:ring-primary/50 focus:outline-none"
                  />
                </div>

                <div>
                  <label className="text-xs text-white/60 mb-1.5 block">Email Address</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    placeholder="john@example.com"
                    data-testid="email-input"
                    className="w-full bg-black/50 border border-white/10 focus:border-primary/50 rounded-lg h-10 px-3 text-sm text-white placeholder:text-white/20 transition-all focus:ring-1 focus:ring-primary/50 focus:outline-none"
                  />
                </div>

                <div>
                  <label className="text-xs text-white/60 mb-1.5 block">Country</label>
                  <select
                    value={formData.country}
                    onChange={(e) => setFormData({...formData, country: e.target.value})}
                    data-testid="country-select"
                    className="w-full bg-black/50 border border-white/10 focus:border-primary/50 rounded-lg h-10 px-3 text-sm text-white transition-all focus:ring-1 focus:ring-primary/50 focus:outline-none"
                  >
                    {countries.map(country => (
                      <option key={country.code} value={country.code}>
                        {country.flag} {country.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div className="bg-black/40 backdrop-blur-xl border border-white/10 rounded-xl p-4 md:p-5">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-10 h-10 bg-primary/20 rounded-lg flex items-center justify-center">
                  <Wallet className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-white">Select Networks</h2>
                  <p className="text-xs text-white/60">Choose blockchain networks for your wallets</p>
                </div>
              </div>

              <div className="grid grid-cols-3 md:grid-cols-5 gap-2">
                {networks.map(network => (
                  <button
                    key={network.id}
                    onClick={() => toggleNetwork(network.id)}
                    data-testid={`network-${network.id}`}
                    className={`p-2.5 rounded-lg border-2 transition-all ${
                      selectedNetworks.includes(network.id)
                        ? 'border-primary bg-primary/10'
                        : 'border-white/10 bg-black/50 hover:border-white/20'
                    }`}
                  >
                    <div className="flex flex-col items-center gap-1">
                      <div className="text-xl">{network.icon}</div>
                      <div className="text-xs font-medium text-white text-center truncate w-full">{network.name}</div>
                      {selectedNetworks.includes(network.id) && (
                        <Check className="w-3 h-3 text-primary" />
                      )}
                    </div>
                  </button>
                ))}
              </div>

              <div className="mt-3 text-xs text-white/60">
                Selected: {selectedNetworks.length} network{selectedNetworks.length !== 1 ? 's' : ''}
              </div>
            </div>

            <button
              onClick={() => setStep(2)}
              disabled={!canProceed}
              data-testid="continue-to-review-btn"
              className="w-full bg-primary hover:bg-primary/90 text-white text-sm font-medium py-3 rounded-full transition-all hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 flex items-center justify-center gap-2"
            >
              Continue to Review
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Step 2: Review */}
        {step === 2 && (
          <div className="bg-black/40 backdrop-blur-xl border border-white/10 rounded-xl p-4 md:p-5">
            <h2 className="text-lg font-semibold text-white mb-4">Review Your Information</h2>

            <div className="space-y-4 mb-6">
              <div>
                <h3 className="text-sm font-semibold text-white mb-2">
                  {accountType === 'personal' ? 'Personal Details' : 'Business Details'}
                </h3>
                <div className="space-y-1.5">
                  {accountType === 'business' && (
                    <>
                      <div className="flex justify-between p-2 bg-white/5 rounded-lg text-sm">
                        <span className="text-white/60">Business Name</span>
                        <span className="text-white">{formData.business_name}</span>
                      </div>
                      <div className="flex justify-between p-2 bg-white/5 rounded-lg text-sm">
                        <span className="text-white/60">Registration #</span>
                        <span className="text-white">{formData.registration_number}</span>
                      </div>
                    </>
                  )}
                  <div className="flex justify-between p-2 bg-white/5 rounded-lg text-sm">
                    <span className="text-white/60">Name</span>
                    <span className="text-white">{formData.full_name}</span>
                  </div>
                  <div className="flex justify-between p-2 bg-white/5 rounded-lg text-sm">
                    <span className="text-white/60">Email</span>
                    <span className="text-white">{formData.email}</span>
                  </div>
                  <div className="flex justify-between p-2 bg-white/5 rounded-lg text-sm">
                    <span className="text-white/60">Country</span>
                    <span className="text-white">
                      {countries.find(c => c.code === formData.country)?.flag} {countries.find(c => c.code === formData.country)?.name}
                    </span>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-sm font-semibold text-white mb-2">Selected Networks ({selectedNetworks.length})</h3>
                <div className="flex flex-wrap gap-1.5">
                  {selectedNetworks.map(netId => {
                    const net = networks.find(n => n.id === netId);
                    return (
                      <div key={netId} className="flex items-center gap-1.5 bg-primary/20 border border-primary/30 px-2 py-1 rounded-lg">
                        <span className="text-base">{net.icon}</span>
                        <span className="text-white text-xs">{net.name}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="bg-primary/10 border border-primary/20 rounded-lg p-3">
                <p className="text-xs text-primary/90">
                  You will receive: 1 Bank Account (IBAN + SWIFT) + {selectedNetworks.length} Crypto Wallets. Supports 300+ tokens across all networks.
                </p>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setStep(1)}
                data-testid="back-to-form-btn"
                className="flex-1 bg-white/5 hover:bg-white/10 text-white text-sm border border-white/10 font-medium py-2.5 rounded-full transition-all flex items-center justify-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </button>
              <button
                onClick={createAccounts}
                disabled={loading}
                data-testid="create-accounts-btn"
                className="flex-1 bg-primary hover:bg-primary/90 text-white text-sm font-medium py-2.5 rounded-full transition-all hover:scale-105 active:scale-95 disabled:opacity-50"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-1.5">
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    Creating...
                  </span>
                ) : (
                  'Create All Accounts'
                )}
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Success */}
        {step === 3 && accounts && (
          <div className="space-y-4">
            <div className="bg-black/40 backdrop-blur-xl border border-white/10 rounded-xl p-5 text-center">
              <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-3">
                <CheckCircle className="w-6 h-6 text-green-500" />
              </div>
              <h2 className="text-lg font-semibold text-white mb-1">All Accounts Created!</h2>
              <p className="text-white/60 text-sm">Your bank account and crypto wallets are ready</p>
            </div>

            {/* Bank Account */}
            <div className="bg-black/40 backdrop-blur-xl border border-white/10 rounded-xl p-4">
              <h3 className="text-base font-semibold text-white mb-3 flex items-center gap-2">
                <Building2 className="w-4 h-4 text-primary" />
                Bank Account (IBAN)
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {accounts.bank_account.iban && (
                  <div>
                    <div className="text-xs text-white/40 mb-1">IBAN</div>
                    <div className="flex items-center gap-2 bg-white/5 rounded-lg p-2">
                      <code className="text-white font-mono text-xs flex-1">{accounts.bank_account.iban}</code>
                      <button onClick={() => copyToClipboard(accounts.bank_account.iban, 'IBAN')} className="text-primary">
                        <Copy className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                )}
                <div>
                  <div className="text-xs text-white/40 mb-1">SWIFT/BIC</div>
                  <div className="flex items-center gap-2 bg-white/5 rounded-lg p-2">
                    <code className="text-white font-mono text-xs flex-1">{accounts.bank_account.swift_bic}</code>
                    <button onClick={() => copyToClipboard(accounts.bank_account.swift_bic, 'SWIFT')} className="text-primary">
                      <Copy className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Crypto Wallets */}
            <div className="bg-black/40 backdrop-blur-xl border border-white/10 rounded-xl p-4">
              <h3 className="text-base font-semibold text-white mb-3 flex items-center gap-2">
                <Wallet className="w-4 h-4 text-primary" />
                Crypto Wallets ({accounts.crypto_wallets ? Object.keys(accounts.crypto_wallets).length : 0} Networks)
              </h3>
              <div className="space-y-2">
                {accounts.crypto_wallets && Object.entries(accounts.crypto_wallets).map(([networkId, wallet]) => {
                  const net = networks.find(n => n.id === networkId);
                  const isExpanded = expandedWallet === networkId;
                  return (
                    <div key={networkId} className="bg-white/5 rounded-lg p-3">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="text-xl">{net?.icon}</span>
                          <div>
                            <div className="text-sm text-white font-medium">{wallet.network}</div>
                            <div className="text-xs text-white/60">{wallet.symbol}</div>
                          </div>
                        </div>
                        <button
                          onClick={() => setExpandedWallet(isExpanded ? null : networkId)}
                          className="text-primary text-xs hover:text-primary/80"
                        >
                          {isExpanded ? 'Hide' : 'Show'} QR
                        </button>
                      </div>
                      <div className="flex items-center gap-2 bg-black/50 rounded-lg p-2">
                        <code className="text-white font-mono text-xs flex-1 break-all">{wallet.address}</code>
                        <button onClick={() => copyToClipboard(wallet.address, wallet.network)} className="text-primary flex-shrink-0">
                          <Copy className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      {isExpanded && (
                        <div className="mt-2 flex justify-center">
                          <div className="bg-white p-2 rounded-lg">
                            <QRCodeSVG value={wallet.address} size={120} level="H" />
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="bg-primary/10 border border-primary/20 rounded-lg p-3">
              <p className="text-xs text-primary/90 text-center">
                ✓ Supports 300+ tokens across all networks • Secure storage • Easy transfers
              </p>
            </div>

            <button
              onClick={() => setStep(4)}
              data-testid="continue-to-kyc-btn"
              className="w-full bg-primary hover:bg-primary/90 text-white text-sm font-medium py-3 rounded-full transition-all hover:scale-105 active:scale-95 flex items-center justify-center gap-2"
            >
              <Shield className="w-4 h-4" />
              Continue to KYC Verification
              <ArrowRight className="w-4 h-4" />
            </button>

            <button
              onClick={() => navigate('/portfolio')}
              className="w-full bg-white/5 hover:bg-white/10 text-white text-sm border border-white/10 font-medium py-2.5 rounded-full transition-all"
            >
              Skip for Now - Go to Dashboard
            </button>
          </div>
        )}

        {/* Step 4: KYC Verification with Sumsub */}
        {step === 4 && (
          <div className="space-y-4">
            <div className="bg-black/40 backdrop-blur-xl border border-white/10 rounded-xl p-4 md:p-5">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center">
                  <Shield className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-white">Identity Verification</h2>
                  <p className="text-xs text-white/60">Complete KYC to unlock all features</p>
                </div>
              </div>

              {/* KYC Status Banner */}
              {kycStatus?.kyc_verified && (
                <div className="bg-green-500/20 border border-green-500/30 rounded-lg p-4 mb-4">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    <span className="text-green-400 font-medium">KYC Verification Approved</span>
                  </div>
                  <p className="text-green-400/70 text-sm mt-1">Your identity has been verified. You have full access to all features.</p>
                </div>
              )}

              {kycStatus?.review_answer === 'RED' && (
                <div className="bg-red-500/20 border border-red-500/30 rounded-lg p-4 mb-4">
                  <div className="flex items-center gap-2">
                    <Shield className="w-5 h-5 text-red-500" />
                    <span className="text-red-400 font-medium">Verification Rejected</span>
                  </div>
                  <p className="text-red-400/70 text-sm mt-1">Please try again with valid documents.</p>
                </div>
              )}

              {/* Sumsub WebSDK Container */}
              {!kycStatus?.kyc_verified && (
                <>
                  {!sumsubToken && !kycLoading && !kycError && (
                    <div className="text-center py-8">
                      <Shield className="w-16 h-16 text-primary/40 mx-auto mb-4" />
                      <h3 className="text-white font-medium mb-2">Ready to Verify Your Identity</h3>
                      <p className="text-white/60 text-sm mb-6 max-w-md mx-auto">
                        This process takes only a few minutes. You'll need a valid government-issued ID and to take a quick selfie.
                      </p>
                      <button
                        onClick={startKycVerification}
                        data-testid="start-kyc-btn"
                        className="bg-primary hover:bg-primary/90 text-white text-sm font-medium py-3 px-8 rounded-full transition-all hover:scale-105 active:scale-95"
                      >
                        Start KYC Verification
                      </button>
                    </div>
                  )}

                  {kycError && (
                    <div className="text-center py-8">
                      <div className="w-16 h-16 bg-yellow-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Shield className="w-8 h-8 text-yellow-500" />
                      </div>
                      <h3 className="text-white font-medium mb-2">KYC Service Temporarily Unavailable</h3>
                      <p className="text-white/60 text-sm mb-4 max-w-md mx-auto">
                        {kycError}
                      </p>
                      <p className="text-white/40 text-xs mb-6 max-w-md mx-auto">
                        You can continue using the platform. KYC verification will be available soon.
                      </p>
                      <button
                        onClick={() => { setKycError(null); startKycVerification(); }}
                        className="bg-white/10 hover:bg-white/20 text-white text-sm font-medium py-2 px-6 rounded-full transition-all mr-3"
                      >
                        Try Again
                      </button>
                      <button
                        onClick={() => navigate('/portfolio')}
                        className="bg-primary hover:bg-primary/90 text-white text-sm font-medium py-2 px-6 rounded-full transition-all"
                      >
                        Go to Dashboard
                      </button>
                    </div>
                  )}

                  {kycLoading && (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="w-8 h-8 text-primary animate-spin" />
                      <span className="ml-3 text-white/60">Initializing verification...</span>
                    </div>
                  )}

                  {sumsubToken && !kycLoading && (
                    <div className="sumsub-container min-h-[500px] bg-white/5 rounded-lg overflow-hidden" data-testid="sumsub-sdk-container">
                      <SumsubWebSdk
                        accessToken={sumsubToken}
                        expirationHandler={handleTokenExpiration}
                        config={{
                          lang: 'en',
                          theme: 'dark'
                        }}
                        options={{
                          addViewportTag: false,
                          adaptIframeHeight: true
                        }}
                        onMessage={handleSumsubMessage}
                        onError={handleSumsubError}
                      />
                    </div>
                  )}
                </>
              )}

              {/* Benefits of KYC */}
              <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="bg-white/5 rounded-lg p-3 text-center">
                  <div className="text-primary text-lg font-bold">$50K</div>
                  <div className="text-xs text-white/60">Daily Limit</div>
                </div>
                <div className="bg-white/5 rounded-lg p-3 text-center">
                  <div className="text-primary text-lg font-bold">Instant</div>
                  <div className="text-xs text-white/60">Withdrawals</div>
                </div>
                <div className="bg-white/5 rounded-lg p-3 text-center">
                  <div className="text-primary text-lg font-bold">Premium</div>
                  <div className="text-xs text-white/60">Support</div>
                </div>
                <div className="bg-white/5 rounded-lg p-3 text-center">
                  <div className="text-primary text-lg font-bold">Cards</div>
                  <div className="text-xs text-white/60">Virtual & Physical</div>
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setStep(3)}
                className="flex-1 bg-white/5 hover:bg-white/10 text-white text-sm border border-white/10 font-medium py-2.5 rounded-full transition-all flex items-center justify-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Wallets
              </button>
              <button
                onClick={() => navigate('/portfolio')}
                data-testid="go-to-dashboard-btn"
                className="flex-1 bg-primary hover:bg-primary/90 text-white text-sm font-medium py-2.5 rounded-full transition-all"
              >
                Go to Dashboard
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GetStarted;
